import { FC, useMemo } from 'react'

import styled from 'styled-components'

import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import Waiting from 'src/Assets/Svg/common/waiting.svg?react'
import Empty from 'src/Assets/Svg/common/empty.svg?react'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
} from 'src/Types/campaigns'
import { CampaignPayment } from 'src/Redux/artistOrderApi/campaignPayment'
import { CampaignPaymentInfo } from 'src/Components/CampaignPaymentInfo'
import dayJs from 'src/Helpers/dayjs'
import { formatDate } from 'src/Constants/formatDate'

import { ApprovedInfoContent } from './ApprovedInfoContent'
import { RunningNoReviewsInfoContent } from './RunningNoReviewsInfoContent'

const MainInfoContainer = styled.div<{
  $approved?: boolean
}>`
  display: flex;
  height: 400px;
  padding: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ $approved }) => ($approved ? '24px' : '16px')};
  border-radius: 16px;
  border: 1px solid
    ${({ theme, $approved }) =>
      $approved ? theme.color.brandGreen : theme.color.lightGrey};
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  width: 100%;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    border: 1px solid
      ${({ theme, $approved }) =>
        $approved ? theme.color.brandGreen : 'transparent'};

    box-shadow: none;
  }
`

const InfoText = styled.span<{
  $wideText?: boolean
}>`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  max-width: ${({ $wideText }) => ($wideText ? 'none' : '400px')};
`

export type InfoBlockProps = {
  campaignStatus?: CampaignStatusEnum
  campaignId?: number
  payment?: CampaignPayment | null
  startDate?: string | null
  reviewsCount?: number
  onStartCampaignClick?: () => void
}

export const InfoBlock: FC<InfoBlockProps> = ({
  campaignStatus,
  campaignId,
  payment,
  startDate,
  reviewsCount,
  onStartCampaignClick,
}) => {
  const approvedStatus = campaignStatus === CampaignStatusEnum.APPROVED
  const formattedStartDate = dayJs(startDate).format(formatDate.formatDate1)
  const noReviews = reviewsCount === 0

  const content = useMemo(() => {
    if (campaignStatus === CampaignStatusEnum.PENDING_APPROVAL) {
      return (
        <>
          <Waiting />
          <InfoText>A&R team will review your track within 15-45 min.</InfoText>
        </>
      )
    }

    if (approvedStatus) {
      return <ApprovedInfoContent onStartCampaignClick={onStartCampaignClick} />
    }

    if (campaignStatus === CampaignStatusEnum.SCHEDULED) {
      return (
        <>
          <Waiting />
          <InfoText>
            {`Your track will be available to curators on ${formattedStartDate}.`}
          </InfoText>
        </>
      )
    }

    if (campaignStatus === CampaignStatusEnum.RUNNING && noReviews) {
      return <RunningNoReviewsInfoContent />
    }

    if (campaignStatus === CampaignStatusEnum.STOPPED && noReviews) {
      return (
        <>
          <Empty />
          <InfoText>
            You stopped the campaign before receiving any feedback from
            curators.
          </InfoText>
        </>
      )
    }

    return null
  }, [
    campaignStatus,
    approvedStatus,
    formattedStartDate,
    noReviews,
    onStartCampaignClick,
  ])

  if (payment && campaignId) {
    return (
      <MainInfoContainer>
        <CampaignPaymentInfo
          payment={payment}
          platform={CampaignPlatformTypeEnum.SPOTIFY}
          campaignId={campaignId}
        />
      </MainInfoContainer>
    )
  }

  if (!content) {
    return null
  }

  return (
    <MainInfoContainer $approved={approvedStatus}>{content}</MainInfoContainer>
  )
}
